import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { Tooltip } from '../components/common/Tooltip';

interface TooltipState {
  visible: boolean;
  content: string;
  x: number;
  y: number;
}

interface TooltipContextType {
  showTooltip: (content: string, x: number, y: number) => void;
  hideTooltip: () => void;
  updatePosition: (x: number, y: number) => void;
  tooltipState: TooltipState;
}

const TooltipContext = createContext<TooltipContextType | undefined>(undefined);

export const TooltipProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tooltipState, setTooltipState] = useState<TooltipState>({
    visible: false,
    content: '',
    x: 0,
    y: 0,
  });

  const showTooltip = useCallback((content: string, x: number, y: number) => {
    setTooltipState({
      visible: true,
      content,
      x,
      y,
    });
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltipState((prev) => ({
      ...prev,
      visible: false,
    }));
  }, []);

  const updatePosition = useCallback((x: number, y: number) => {
    setTooltipState((prev) => ({
      ...prev,
      x,
      y,
    }));
  }, []);

  const contextValue = useMemo(
    () => ({ showTooltip, hideTooltip, updatePosition, tooltipState }),
    [showTooltip, hideTooltip, updatePosition, tooltipState]
  );

  return (
    <TooltipContext.Provider value={contextValue}>
      {children}
      <Tooltip
        content={tooltipState.content}
        x={tooltipState.x}
        y={tooltipState.y}
        visible={tooltipState.visible}
      />
    </TooltipContext.Provider>
  );
};

export const useTooltip = (): TooltipContextType => {
  const context = useContext(TooltipContext);
  if (context === undefined) {
    throw new Error('useTooltip must be used within a TooltipProvider');
  }
  return context;
};
